// Workflow state machine. Authoritative source of legal transitions.
// Imported by the API to enforce, and by the Web to grey out illegal buttons.

import type { EventType, Role, Status } from './types.js';

export interface Transition {
  from: Status;
  event: EventType;
  to: Status;
  /** Roles allowed to fire this event. */
  allowedRoles: readonly Role[];
  /** Whether this transition closes the request (sets closed_at). */
  closes?: boolean;
  /** Whether this event requires a comment string. */
  requiresComment?: boolean;
  /** Whether this event requires a conditions string. */
  requiresConditions?: boolean;
}

/**
 * The full transition table. To add a state or event, add a row here —
 * both API and Web pick up the change without further coordination.
 */
export const TRANSITIONS: readonly Transition[] = [
  // ── IT review ──
  {
    from: 'pending_it_review',
    event: 'it_approved',
    to: 'pending_legal_review',
    allowedRoles: ['it_reviewer', 'admin'],
    requiresComment: true,
  },
  {
    from: 'pending_it_review',
    event: 'it_rejected',
    to: 'rejected',
    allowedRoles: ['it_reviewer', 'admin'],
    closes: true,
    requiresComment: true,
  },
  {
    from: 'pending_it_review',
    event: 'it_clarification_requested',
    to: 'it_clarification_requested',
    allowedRoles: ['it_reviewer', 'admin'],
    requiresComment: true,
  },

  // ── Submitter responds to IT clarification ──
  {
    from: 'it_clarification_requested',
    event: 'submitter_responded',
    to: 'pending_it_review',
    allowedRoles: ['employee', 'it_reviewer', 'legal_reviewer', 'admin'],
    requiresComment: true,
  },

  // ── Legal review ──
  {
    from: 'pending_legal_review',
    event: 'legal_approved',
    to: 'approved',
    allowedRoles: ['legal_reviewer', 'admin'],
    closes: true,
    requiresComment: true,
  },
  {
    from: 'pending_legal_review',
    event: 'legal_approved_with_conditions',
    to: 'approved_with_conditions',
    allowedRoles: ['legal_reviewer', 'admin'],
    closes: true,
    requiresComment: true,
    requiresConditions: true,
  },
  {
    from: 'pending_legal_review',
    event: 'legal_rejected',
    to: 'rejected',
    allowedRoles: ['legal_reviewer', 'admin'],
    closes: true,
    requiresComment: true,
  },
  {
    from: 'pending_legal_review',
    event: 'legal_clarification_requested',
    to: 'legal_clarification_requested',
    allowedRoles: ['legal_reviewer', 'admin'],
    requiresComment: true,
  },

  // ── Submitter responds to Legal clarification ──
  {
    from: 'legal_clarification_requested',
    event: 'submitter_responded',
    to: 'pending_legal_review',
    allowedRoles: ['employee', 'it_reviewer', 'legal_reviewer', 'admin'],
    requiresComment: true,
  },
];

/** All initial submissions land in this status. */
export const INITIAL_STATUS: Status = 'pending_it_review';

/** Terminal statuses — no further transitions accepted. */
export const TERMINAL_STATUSES: readonly Status[] = [
  'approved',
  'approved_with_conditions',
  'rejected',
];

export function isTerminal(status: Status): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export interface TransitionAttempt {
  from: Status;
  event: EventType;
  role: Role;
  isSubmitter?: boolean;
  comment?: string | null;
  conditions?: string | null;
}

export interface TransitionResult {
  ok: boolean;
  to?: Status;
  closes?: boolean;
  reason?: string;
}

/**
 * Validate a proposed transition. Returns the resulting status and metadata,
 * or a structured failure reason that callers can surface to the user.
 */
export function evaluateTransition(attempt: TransitionAttempt): TransitionResult {
  const t = TRANSITIONS.find((x) => x.from === attempt.from && x.event === attempt.event);
  if (!t) {
    return { ok: false, reason: `Event "${attempt.event}" is not valid from status "${attempt.from}".` };
  }
  if (!t.allowedRoles.includes(attempt.role)) {
    return { ok: false, reason: `Role "${attempt.role}" cannot fire "${attempt.event}".` };
  }
  // Clarification responses are submitter-only (admins can also act on their behalf).
  if (attempt.event === 'submitter_responded' && attempt.role === 'employee' && !attempt.isSubmitter) {
    return { ok: false, reason: 'Only the original submitter may respond to a clarification.' };
  }
  if (t.requiresComment && !(attempt.comment && attempt.comment.trim())) {
    return { ok: false, reason: `Event "${attempt.event}" requires a comment.` };
  }
  if (t.requiresConditions && !(attempt.conditions && attempt.conditions.trim())) {
    return { ok: false, reason: `Event "${attempt.event}" requires conditions text.` };
  }
  return { ok: true, to: t.to, closes: !!t.closes };
}

/** All events legal from a given status for a given role — used by the web to render action panels. */
export function allowedEvents(status: Status, role: Role): EventType[] {
  return TRANSITIONS.filter((t) => t.from === status && t.allowedRoles.includes(role)).map(
    (t) => t.event,
  );
}

/** Stage index for the UI tracker: 0=submitted, 1=IT, 2=Legal, 3=decision. */
export function stageFor(status: Status): 0 | 1 | 2 | 3 {
  switch (status) {
    case 'pending_it_review':
    case 'it_clarification_requested':
      return 1;
    case 'pending_legal_review':
    case 'legal_clarification_requested':
      return 2;
    case 'approved':
    case 'approved_with_conditions':
    case 'rejected':
      return 3;
  }
}

export const STATUS_LABELS: Record<Status, string> = {
  pending_it_review: 'Pending IT Review',
  it_clarification_requested: 'IT Needs Clarification',
  pending_legal_review: 'Pending Legal Review',
  legal_clarification_requested: 'Legal Needs Clarification',
  approved: 'Approved',
  approved_with_conditions: 'Approved With Conditions',
  rejected: 'Rejected',
};

export const STATUS_BADGE_KEY: Record<Status, string> = {
  pending_it_review: 'pending-it',
  it_clarification_requested: 'needs-info',
  pending_legal_review: 'pending-legal',
  legal_clarification_requested: 'needs-info',
  approved: 'approved',
  approved_with_conditions: 'conditional',
  rejected: 'rejected',
};
